import { defineStore } from "pinia";
import { Board, BoardDetails, dayHourSettings, DefaultDateDaysPayload, TransportInfoStatus } from "@/types/board";
import { BoardProfiles, Boards, ConfigProfile } from "@/types/configProfile";
import { getBrowserLang } from "@/use/useBrowserLang";
import {
  add,
  addBusinessDays,
  differenceInBusinessDays,
  differenceInDays,
  eachWeekendOfInterval,
  getHours,
  getMinutes,
  isBefore,
  isWeekend,
  parseISO
} from "date-fns";
import { setDateHoursToZero } from "@/use/useDate";
import { sortBy } from "@/use/useObject";
import { TimeTicker } from "@/types/timeTicker";
import configProfileService from "@/services/configProfileService";
import { useChatStore } from "@/store/chat/ChatStore";
import boardService from "@/services/boardService";
import { G11_WEB_DOMAIN_ALIAS, G11_WEB_URL, G11_WINDOW_HEIGHT, G11_WINDOW_WIDTH } from "@/env_config";
import { searchIndex } from "@/store/board/useSearchIndex";
import { i18n } from "@/i18n/i18n";
import { useFleetStore } from "@/store/fleet/FleetStore";

interface BoardStoreState {
  fromDate: Date;
  toDate: Date;
  fromTime: number;
  toTime: number;
  days: number;
  today: Date;
  showWeekends: boolean;
  showPreviousLegDetail: boolean;
  transportInfoStatus: TransportInfoStatus | null;
  isEditModeActive: boolean;
  boardProfiles: ConfigProfile[];
  boards: Board[];
  boardsSelected: Board[] | null;
  language: string;
  daysFromTodayStart: number | null;
  daysFromTodayEnd: number | null;
  spottedIds: string[];
  focusedId: string | undefined;
  board: {
    offsetWidth: number;
    boardReady: boolean;
    boxMinWidth: number;
    zoomLevel: number;
    boxExtraMinWidth: number;
  };
  showLostConnectionDialog: boolean;
}

const BOARD_ZOOM_LEVEL = "TP_BOARD_ZOOM_LEVEL";
const USE_NEW_WEBSOCKET_LOGIC = "USE_NEW_WEBSOCKET_LOGIC";
const G11_WINDOW_NAME = `${G11_WEB_DOMAIN_ALIAS}_windowForPostMessages`;

declare global {
  interface Window {
    g11Window: any;
  }
}

const recalculateDateSpan = (
  from: Date,
  to: Date,
  allowWeekends: boolean
): {
  fromDate: Date;
  toDate: Date;
} => {
  const fromDate = !allowWeekends && isWeekend(from) ? addBusinessDays(from, 1) : from;
  const toDate = !allowWeekends && isWeekend(to) ? addBusinessDays(to, -1) : to;

  if (fromDate > toDate) {
    return {
      fromDate: toDate,
      toDate: fromDate
    };
  }

  return {
    fromDate,
    toDate
  };
};

export const useBoardStore = defineStore("board", {
  state: (): BoardStoreState => ({
    fromDate: new Date(),
    toDate: new Date(),
    fromTime: dayHourSettings.DEFAULT_DAY_START,
    toTime: dayHourSettings.DEFAULT_DAY_END,
    days: 1,
    today: new Date(),
    showWeekends: false,
    showPreviousLegDetail: false,
    transportInfoStatus: null,
    isEditModeActive: false,
    boardProfiles: [],
    boards: [],
    boardsSelected: null,
    language: getBrowserLang(),
    daysFromTodayStart: null,
    daysFromTodayEnd: null,
    spottedIds: [],
    focusedId: undefined,
    board: {
      offsetWidth: 0,
      boardReady: false,
      boxMinWidth: 3,
      zoomLevel: 1,
      boxExtraMinWidth: 1
    },
    showLostConnectionDialog: false
  }),
  getters: {
    boardWidth: state => state.board.offsetWidth,
    zoomLevel: state => {
      const zoomLevel = localStorage.getItem(BOARD_ZOOM_LEVEL);

      if (zoomLevel != undefined) {
        return parseInt(zoomLevel);
      }

      return state.board.zoomLevel;
    },
    useNewWebsocketLogic() {
      const useNewWebsocketLogic = localStorage.getItem(USE_NEW_WEBSOCKET_LOGIC);
      if (useNewWebsocketLogic != null && useNewWebsocketLogic == "true") {
        return true;
      }
      return false;
    },
    hourBoxMinWidth: state => {
      const { zoomLevel, boxMinWidth, boxExtraMinWidth } = state.board;
      if (zoomLevel === 0) {
        return boxExtraMinWidth;
      }
      return boxMinWidth * zoomLevel;
    },
    hourBoxExtraMinWidth: state => state.board.boxExtraMinWidth,
    timeTicker(state): TimeTicker {
      const ticker = {
        show: false,
        offset: 0
      };
      // check it today date is between selected dates
      const hour = getHours(state.today);
      const minutes = getMinutes(state.today);
      const todayStart = new Date(setDateHoursToZero(state.today));
      if (state.fromDate <= todayStart && todayStart <= state.toDate) {
        //check if actual time is between selected time
        const toTime = state.fromTime + this.filteredHoursAmount;
        if (state.fromTime <= hour && hour < toTime) {
          let dayDifference = differenceInDays(state.today, state.fromDate);
          if (!state.showWeekends) {
            dayDifference =
              dayDifference -
              eachWeekendOfInterval({
                start: state.fromDate,
                end: state.today
              }).length;
          }

          const daysOffsetInHours = dayDifference * this.filteredHoursAmount;
          const hourDifference = hour - state.fromTime + daysOffsetInHours;
          const minutesOffset = (this.hourBoxWidth / 60) * minutes;
          ticker.offset = hourDifference * this.hourBoxWidth + minutesOffset;
          ticker.show = true;
        }
      }
      return ticker;
    },
    hourBoxWidth(state): number {
      return state.board.offsetWidth / this.hoursDifference;
    },
    daysDifference: state => {
      const daysDifference =
        differenceInDays(state.toDate, state.fromDate) !== 0
          ? differenceInDays(add(state.toDate, { days: 1 }), state.fromDate)
          : state.days;

      return state.showWeekends
        ? daysDifference
        : daysDifference -
            eachWeekendOfInterval({
              start: state.fromDate,
              end: state.toDate
            }).length;
    },
    boardId: state => {
      const boardIds: string[] | undefined = state.boardsSelected
        ?.map(board => board.id)
        .concat()
        .sort(sortBy("boardId"));
      return boardIds?.join(",") || "";
    },
    selectedBoardIds: state => state.boardsSelected?.map(board => board.id) || [],
    hourFromToTime: state => {
      return {
        fromTime: state.fromTime,
        toTime: state.toTime
      };
    },
    fromToDate: state => {
      return {
        fromDate: state.fromDate,
        toDate: state.toDate
      };
    },
    fromToDateWithTime: state => {
      return {
        fromDate: new Date(state.fromDate).setHours(state.fromTime),
        toDate: new Date(state.toDate).setHours(state.toTime)
      };
    },
    filteredHoursAmount: state => {
      let hours: number = state.toTime - state.fromTime;
      //check if hours can be divided by 3 (needed for the 3 hours view or it will break the layout
      let numberToCheck = hours / 3;
      while (!Number.isInteger(numberToCheck)) {
        hours++;
        numberToCheck = hours / 3;
      }
      return hours;
    },
    hoursDifference(): number {
      return this.filteredHoursAmount * this.daysDifference;
    },
    boardDetails(state): BoardDetails {
      const boardFromDate = state.fromDate;
      const boardToDate = state.toDate;
      const boardFromTime = state.fromTime;
      const boardToTime = state.toTime;
      const hourBoxWidth = this.hourBoxWidth;
      const boardWidth = this.boardWidth;

      const timeDiff = boardToTime - boardFromTime;
      const hourBoxesPerDay = (timeDiff % 3 > 0 ? Math.floor(timeDiff / 3) + 1 : timeDiff / 3) * 3;

      return {
        boardFromDate,
        boardToDate,
        boardFromTime,
        boardToTime,
        hourBoxWidth,
        boardWidth,
        hourBoxesPerDay
      };
    },
    isDefaultDateActive: state => state.daysFromTodayStart != null && state.daysFromTodayEnd != null
  },
  actions: {
    setDefaultDateDayAmounts(payload: DefaultDateDaysPayload) {
      const { daysFromTodayStart, daysFromTodayEnd } = payload;
      this.daysFromTodayStart = daysFromTodayStart;
      this.daysFromTodayEnd = daysFromTodayEnd;
    },
    setBoard() {
      setTimeout(() => {
        const boardWidth = document.getElementById("board")?.getBoundingClientRect().width;
        this.board.offsetWidth = boardWidth || 0;
        this.board.boardReady = true;
      }, 0);
    },
    setFromToTime(payload: { fromTime: number; toTime: number }) {
      this.fromTime = payload.fromTime;
      this.toTime = payload.toTime;
    },
    setDates(payload: { fromDate: Date; toDate: Date }) {
      const { fromDate, toDate } = payload;
      this.fromDate = new Date(setDateHoursToZero(fromDate));
      this.toDate = new Date(setDateHoursToZero(toDate));
    },
    initBoardProfiles(payload: BoardProfiles) {
      const array = payload.activeBoardId.split(",");
      const filtered = array.filter(boardId => boardId !== "");
      this.boardsSelected = filtered.map(boardId => {
        return { id: boardId, name: boardId } as Board;
      });
      this.boardProfiles = payload.boardProfiles;
    },
    updateBoardProfiles(payload: ConfigProfile) {
      //check for already saved boards with id
      const index = this.boardProfiles.findIndex(board => board.boardId === payload.boardId);
      if (index !== -1) {
        //if existing update entry
        this.boardProfiles[index] = payload;
      } else {
        //if not add new boardselection
        this.boardProfiles.push(payload);
      }
    },
    setLanguage(value: string) {
      this.language = value;
      i18n.locale = value;
    },
    setZoomLevel(value: number) {
      this.board.zoomLevel = value;
      //set the zoomLevel in localStorage as well
      localStorage.setItem(BOARD_ZOOM_LEVEL, value.toString());
    },
    async toggleShowWeekends() {
      const showWeekends = !this.showWeekends;
      this.showWeekends = showWeekends;
      const boardDateSpan = recalculateDateSpan(this.fromDate, this.toDate, showWeekends);
      this.setDates(boardDateSpan);
    },
    async toggleNewWebsocketLogic() {
      localStorage.setItem(USE_NEW_WEBSOCKET_LOGIC, String(!this.useNewWebsocketLogic));
      window.location.reload();
    },
    toggleShowPreviousLegDetail() {
      this.showPreviousLegDetail = !this.showPreviousLegDetail;
    },
    updateLanguage(value: string) {
      this.setLanguage(value);
      this.updateConfigProfile();
    },
    initTimeTicker() {
      setInterval(() => {
        this.today = new Date();
      }, 60 * 1000);
    },
    async initUserConfigProfile() {
      try {
        const fleetStore = useFleetStore();
        const { data } = await configProfileService.getUserConfigProfiles();
        this.initBoardProfiles(data);
        fleetStore.initTrailerProfile(data);
        useChatStore().initChatUsername();

        this.setLanguage(data.language);
        this.initBoardDateTime(data);
        const array = data.activeBoardId.split(",");
        const boardIds = array.filter(boardId => boardId !== "");
        if (boardIds.length > 0) {
          await fleetStore.fetchBoardFleet(boardIds);
        }
      } catch (e) {
        //show errors which are not handled globally
      }
    },
    initBoardDateTime(payload: BoardProfiles) {
      const activeProfile = payload.boardProfiles.find(profile => profile.boardId === payload.activeBoardId);
      if (typeof activeProfile !== "undefined") {
        const {
          boardStartsAt,
          boardEndsAt,
          dayStartsAt,
          dayEndsAt,
          showWeekends,
          daysFromTodayStart,
          daysFromTodayEnd
        } = activeProfile;

        this.showWeekends = showWeekends!;

        if (daysFromTodayStart != null && daysFromTodayEnd != null) {
          const days = {
            daysFromTodayStart,
            daysFromTodayEnd
          };
          this.setDefaultDates(days);
        } else {
          const dates = {
            fromDate: boardStartsAt != null ? parseISO(boardStartsAt) : new Date(),
            toDate: boardEndsAt != null ? parseISO(boardEndsAt) : new Date()
          };
          this.updateFromToDates(dates);
        }

        const hours = {
          fromTime: dayStartsAt != null ? parseInt(dayStartsAt) : 5,
          toTime: dayEndsAt != null ? parseInt(dayEndsAt) : 20
        };

        this.updateFromToTime(hours);
      }
    },
    async updateConfigProfile(trailers?: string[] | null) {
      const boardId = this.boardId;
      const boardProfile = {
        boardId,
        boardStartsAt: this.fromDate.toISOString(),
        boardEndsAt: this.toDate.toISOString(),
        dayStartsAt: this.fromTime.toString(),
        dayEndsAt: this.toTime.toString(),
        resolutionHours: "string",
        showWeekends: this.showWeekends,
        daysFromTodayStart: this.daysFromTodayStart,
        daysFromTodayEnd: this.daysFromTodayEnd,
        trailerTypes: trailers ? trailers : []
      } as ConfigProfile;
      this.updateBoardProfiles(boardProfile);
      try {
        const payload = {
          activeBoardId: boardId,
          boardProfiles: this.boardProfiles,
          language: this.language
        };
        await configProfileService.updateUserConfigProfiles(payload);
      } catch (e) {
        //show errors which are not handled globally
      }
    },
    async getFilteredBoardProfile() {
      //check for already saved boards with id
      const index = this.boardProfiles.findIndex(board => board.boardId === this.boardId);

      if (index === -1) {
        await this.updateConfigProfile();
      } else {
        const { boardStartsAt, boardEndsAt, dayStartsAt, dayEndsAt } = this.boardProfiles[index];

        const dates = {
          fromDate: boardStartsAt != null ? parseISO(boardStartsAt) : new Date(),
          toDate: boardEndsAt != null ? parseISO(boardEndsAt) : new Date()
        };
        await this.updateFromToDates(dates);

        const time = {
          fromTime: dayStartsAt != null ? parseInt(dayStartsAt) : 5,
          toTime: dayEndsAt != null ? parseInt(dayEndsAt) : 20
        };
        await this.updateFromToTime(time);
      }
    },
    async initBoardsDropdown() {
      try {
        const { data } = await boardService.getBoards();
        this.boards = data.boards;
      } catch (e) {
        //show errors which are not handled globally
      }
    },
    async initFromToDates() {
      const dates = {
        fromDate: new Date(),
        toDate: new Date()
      };
      this.setDates(dates);
    },
    async updateFromToDates(payload: { fromDate: Date; toDate: Date }) {
      this.setDates(payload);
      this.setBoard();
    },
    async updateFromToTime(payload: { fromTime: number; toTime: number }) {
      this.setFromToTime(payload);
      this.setBoard();
    },
    toggleBoardEditMode() {
      this.isEditModeActive = !this.isEditModeActive;
      if (this.isEditModeActive) {
        this.transportInfoStatus = { type: "default", value: "default" };
      } else {
        this.transportInfoStatus = null;
      }
    },
    async setDefaultDates(payload: DefaultDateDaysPayload) {
      this.setDefaultDateDayAmounts(payload);
      const today = new Date();
      let dates = {
        fromDate: add(today, { days: payload.daysFromTodayStart || 0 }),
        toDate: add(today, { days: payload.daysFromTodayEnd || 0 })
      };
      if (!this.showWeekends) {
        let fromDiff = differenceInBusinessDays(today, dates.fromDate);
        if (isBefore(dates.fromDate, today)) {
          fromDiff *= -1;
        }
        let toDiff = differenceInBusinessDays(today, dates.toDate);
        if (isBefore(today, dates.toDate)) {
          toDiff *= -1;
        }
        dates = {
          fromDate: addBusinessDays(today, fromDiff),
          toDate: addBusinessDays(today, toDiff)
        };
      }

      this.updateFromToDates(dates);
    },
    async postG11Message(message: any) {
      try {
        if (!window.g11Window || window.g11Window.closed) {
          const g11Url = new URL(G11_WEB_URL);
          g11Url.searchParams.append("tp-record", message.data.record);
          g11Url.searchParams.append("tp-screen", message.data.screen);
          g11Url.searchParams.append("tp-leg-id", message.data.parameters.TR_LEG_ID);
          window.g11Window = window.open(
            g11Url.href,
            G11_WINDOW_NAME,
            "menubar = no, resizable = yes, scrollbars = yes, status = no, toolbar = no"
          );
          window.g11Window.resizeTo(G11_WINDOW_WIDTH, G11_WINDOW_HEIGHT);
          return;
        }

        window.g11Window.focus();
        window.g11Window.postMessage(message, G11_WEB_URL);
      } catch (e) {
        console.error("Failed to notify G11 Web", e);
      }
    },
    async persistBoardConfig(orderedSidebarTiles: string[]): Promise<void> {
      try {
        const boardPayload: Boards = {
          boardId: this.boardId,
          sidebarTiles: orderedSidebarTiles
        };
        await configProfileService.updateBoardConfig(boardPayload);
      } catch (e) {
        //show errors which are not handled globally
      }
    },
    async searchBoardEntities(searchTerm: string): Promise<string[]> {
      const matchedIds = await searchIndex(searchTerm);

      this.spottedIds = matchedIds;

      return Promise.resolve(matchedIds);
    }
  }
});
